import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import styled from "styled-components";
import PlayIcon from "assets/play-player.svg";

type Props = {
  open: boolean;
  onClose: () => void;
  onRecorded?: (file: File) => void;
};

export const RecordAudioModal: React.FC<Props> = ({ open, onClose, onRecorded }) => {
  const [isRecording, setIsRecording] = useState(false);
  const [seconds, setSeconds] = useState(0);
  const [hasRecording, setHasRecording] = useState(false);
  const [isPlayingBack, setIsPlayingBack] = useState(false);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const timerRef = useRef<number | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const sourceRef = useRef<MediaStreamAudioSourceNode | null>(null);
  const waveformCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const animationFrameRef = useRef<number | null>(null);
  const recordedChunksRef = useRef<Blob[]>([]);
  const waveformHistoryRef = useRef<number[] | null>(null);
  const frameCounterRef = useRef(0);
  const recordedBlobRef = useRef<Blob | null>(null);
  const playbackAudioRef = useRef<HTMLAudioElement | null>(null);
  const playbackUrlRef = useRef<string | null>(null);
  const staticWaveformRef = useRef<{ min: number; max: number }[] | null>(null);
  const playbackAnimationFrameRef = useRef<number | null>(null);
  const playbackDurationRef = useRef<number>(0);
  const isScrubbingRef = useRef(false);
  const wasPlayingBeforeScrubRef = useRef(false);

  const formatTime = (totalSeconds: number) => {
    const minutes = Math.floor(totalSeconds / 60)
      .toString()
      .padStart(2, "0");
    const sec = (totalSeconds % 60).toString().padStart(2, "0");
    return `${minutes}:${sec}`;
  };

  const cleanup = useCallback(() => {
    if (timerRef.current) {
      window.clearInterval(timerRef.current);
      timerRef.current = null;
    }
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }
    if (audioContextRef.current) {
      audioContextRef.current.close().catch(() => {});
      audioContextRef.current = null;
    }
    analyserRef.current = null;
    sourceRef.current = null;
    if (mediaRecorderRef.current?.stream) {
      mediaRecorderRef.current.stream.getTracks().forEach((track) => track.stop());
    }
    mediaRecorderRef.current = null;
    if (playbackAnimationFrameRef.current) {
      cancelAnimationFrame(playbackAnimationFrameRef.current);
      playbackAnimationFrameRef.current = null;
    }
    if (playbackAudioRef.current) {
      playbackAudioRef.current.pause();
      playbackAudioRef.current = null;
    }
    if (playbackUrlRef.current) {
      URL.revokeObjectURL(playbackUrlRef.current);
      playbackUrlRef.current = null;
    }
    staticWaveformRef.current = null;
    playbackDurationRef.current = 0;
    setIsPlayingBack(false);
    setIsRecording(false);
  }, []);

  const stopRecording = useCallback(() => {
    if (!mediaRecorderRef.current) return;
    mediaRecorderRef.current.stop();
  }, []);

  const drawWaveform = useCallback(() => {
    const canvas = waveformCanvasRef.current;
    const analyser = analyserRef.current;
    if (!canvas || !analyser) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const width = canvas.width;
    const height = canvas.height;

    const HISTORY_POINTS = 300; // ~10 сек при 60 FPS и сдвиге каждые 2 кадра
    const SCROLL_EVERY_N_FRAMES = 2;

    if (!waveformHistoryRef.current || waveformHistoryRef.current.length !== HISTORY_POINTS) {
      waveformHistoryRef.current = new Array(HISTORY_POINTS).fill(0.5); // центр
    }

    const bufferLength = analyser.fftSize;
    const dataArray = new Uint8Array(bufferLength);

    const render = () => {
      if (!analyserRef.current || !waveformCanvasRef.current || !waveformHistoryRef.current) return;

      analyser.getByteTimeDomainData(dataArray);

      // Быстрое обновление амплитуды: берём несколько точек и усредняем
      const sampleCount = 32;
      const step = Math.floor(bufferLength / sampleCount) || 1;
      let sum = 0;
      for (let i = 0; i < sampleCount; i++) {
        sum += dataArray[i * step];
      }
      const avg = sum / sampleCount;
      const normalized = avg / 255; // 0..1

      frameCounterRef.current += 1;
      if (frameCounterRef.current % SCROLL_EVERY_N_FRAMES === 0) {
        // Сдвигаем историю влево и добавляем новую точку в конец
        waveformHistoryRef.current.shift();
        waveformHistoryRef.current.push(normalized);
      } else {
        // Обновляем только последнюю точку, чтобы волна "дрожала" быстрее
        waveformHistoryRef.current[HISTORY_POINTS - 1] = normalized;
      }

      ctx.clearRect(0, 0, width, height);
      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, width, height);

      ctx.lineWidth = 2;
      ctx.strokeStyle = "#111827";
      ctx.beginPath();

      const sliceWidth = width / (HISTORY_POINTS - 1);
      let x = 0;

      waveformHistoryRef.current.forEach((value, index) => {
        // Нормализуем вокруг центра и сильно усиливаем амплитуду,
        // но ограничиваем её, чтобы не выходила за границы канваса
        const centered = Math.max(-0.5, Math.min(0.5, value - 0.5));
        const boosted = centered * 10; // усиливаем ~в 3.2 раза
        const y = height / 2 - boosted * (height * 0.9);

        if (index === 0) {
          ctx.moveTo(x, y);
        } else {
          ctx.lineTo(x, y);
        }

        x += sliceWidth;
      });

      ctx.stroke();

      animationFrameRef.current = requestAnimationFrame(render);
    };

    render();
  }, []);

  const drawPlaybackFrame = useCallback(() => {
    const canvas = waveformCanvasRef.current;
    const segments = staticWaveformRef.current;
    if (!canvas || !segments) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const width = canvas.width;
    const height = canvas.height;

    // фон
    ctx.clearRect(0, 0, width, height);
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, width, height);

    // рисуем сохранённый вейв по всей ширине
    ctx.strokeStyle = "#111827";
    ctx.lineWidth = 1;
    ctx.beginPath();
    segments.forEach((seg, x) => {
      const min = seg.min;
      const max = seg.max;
      const yMax = ((1 - (max + 1) / 2) * height);
      const yMin = ((1 - (min + 1) / 2) * height);
      ctx.moveTo(x, yMin);
      ctx.lineTo(x, yMax);
    });
    ctx.stroke();

    // палочка-плейхед
    const audio = playbackAudioRef.current;
    const duration = playbackDurationRef.current || seconds || 1;
    if (audio && duration > 0) {
      const ratio = Math.min(1, Math.max(0, audio.currentTime / duration));
      const xPos = ratio * width;
      ctx.strokeStyle = "#4b5563";
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(xPos, 0);
      ctx.lineTo(xPos, height);
      ctx.stroke();

      if (!audio.paused && !audio.ended) {
        playbackAnimationFrameRef.current =
          requestAnimationFrame(drawPlaybackFrame);
      } else {
        playbackAnimationFrameRef.current = null;
      }
    }
  }, [seconds]);

  const renderStaticWaveformFromBlob = useCallback(
    async (blob: Blob) => {
      try {
        const arrayBuffer = await blob.arrayBuffer();
        const AudioContextCtor =
          (window as any).AudioContext || (window as any).webkitAudioContext;
        const audioContext: AudioContext = new AudioContextCtor();
        const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);

        const canvas = waveformCanvasRef.current;
        if (!canvas) {
          audioContext.close();
          return;
        }
        const width = canvas.width;
        const height = canvas.height;
        const data = audioBuffer.getChannelData(0);
        const samplesPerPixel = Math.floor(data.length / width) || 1;

        const segments: { min: number; max: number }[] = new Array(width);
        for (let x = 0; x < width; x++) {
          const start = x * samplesPerPixel;
          const end = Math.min(start + samplesPerPixel, data.length);
          let min = 1.0;
          let max = -1.0;
          for (let i = start; i < end; i++) {
            const v = data[i];
            if (v < min) min = v;
            if (v > max) max = v;
          }
          segments[x] = { min, max };
        }

        staticWaveformRef.current = segments;
        playbackDurationRef.current = audioBuffer.duration;
        audioContext.close();

        // первый рендер статического вейва без палочки
        drawPlaybackFrame();
      } catch (err) {
        console.error("Failed to render static waveform:", err);
      }
    },
    [drawPlaybackFrame]
  );

  const startRecording = useCallback(async () => {
    if (isRecording) {
      stopRecording();
      return;
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream);
      recordedChunksRef.current = [];
      mediaRecorderRef.current = mediaRecorder;
      setSeconds(0);
      setIsRecording(true);

      mediaRecorder.ondataavailable = (event: BlobEvent) => {
        if (event.data && event.data.size > 0) {
          recordedChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = () => {
        const blob = new Blob(recordedChunksRef.current, { type: "audio/webm" });
        recordedChunksRef.current = [];
        if (blob.size > 0) {
          recordedBlobRef.current = blob;
          setHasRecording(true);
          // построить статический вейв по всей длине записи
          void renderStaticWaveformFromBlob(blob);
        }
        cleanup();
      };

      mediaRecorder.start();

      const timerId = window.setInterval(() => {
        setSeconds((prev) => {
          const next = prev + 1;
          if (next >= 59) {
            stopRecording();
            return 59;
          }
          return next;
        });
      }, 1000);
      timerRef.current = timerId;

      const AudioContextCtor =
        (window as any).AudioContext || (window as any).webkitAudioContext;
      const audioContext: AudioContext = new AudioContextCtor();
      const source = audioContext.createMediaStreamSource(stream);
      const analyser = audioContext.createAnalyser();
      analyser.fftSize = 2048;
      source.connect(analyser);

      audioContextRef.current = audioContext;
      analyserRef.current = analyser;
      sourceRef.current = source;

      drawWaveform();
    } catch (error) {
      console.error("Failed to start recording:", error);
      cleanup();
      onClose();
    }
  }, [cleanup, drawWaveform, isRecording, onClose, stopRecording]);

  useEffect(() => {
    if (!open) {
      cleanup();
      setSeconds(0);
      setHasRecording(false);
      recordedBlobRef.current = null;
      waveformHistoryRef.current = null;
      return;
    }
    // при каждом открытии начинаем с пустой, ровной линии
    setSeconds(0);
    setIsRecording(false);
    setHasRecording(false);
    setIsPlayingBack(false);
    recordedBlobRef.current = null;
    waveformHistoryRef.current = new Array(300).fill(0.5);
    staticWaveformRef.current = null;
    playbackDurationRef.current = 0;
    isScrubbingRef.current = false;
    wasPlayingBeforeScrubRef.current = false;
  }, [cleanup, open]);

  useEffect(
    () => () => {
      cleanup();
    },
    [cleanup]
  );

  if (!open) return null;

  const handleOverlayClick = () => {
    if (isRecording) {
      stopRecording();
    }
    if (playbackAnimationFrameRef.current) {
      cancelAnimationFrame(playbackAnimationFrameRef.current);
      playbackAnimationFrameRef.current = null;
    }
    if (playbackAudioRef.current) {
      playbackAudioRef.current.pause();
    }
    setIsPlayingBack(false);
    onClose();
  };

  const ensurePlaybackAudio = () => {
    if (!recordedBlobRef.current) return null;
    let audio = playbackAudioRef.current;
    if (!audio) {
      const url =
        playbackUrlRef.current || URL.createObjectURL(recordedBlobRef.current);
      if (!playbackUrlRef.current) {
        playbackUrlRef.current = url;
      }
      audio = new Audio(url);
      playbackAudioRef.current = audio;
      audio.addEventListener("ended", () => {
        setIsPlayingBack(false);
        if (playbackAnimationFrameRef.current) {
          cancelAnimationFrame(playbackAnimationFrameRef.current);
          playbackAnimationFrameRef.current = null;
        }
        drawPlaybackFrame();
      });
    }
    return audio;
  };

  const handleTogglePlayback = () => {
    const audio = ensurePlaybackAudio();
    if (!audio) return;

    if (!audio.paused && !audio.ended) {
      audio.pause();
      setIsPlayingBack(false);
      if (playbackAnimationFrameRef.current) {
        cancelAnimationFrame(playbackAnimationFrameRef.current);
        playbackAnimationFrameRef.current = null;
      }
      drawPlaybackFrame();
      return;
    }

    audio
      .play()
      .then(() => {
        setIsPlayingBack(true);
        if (playbackAnimationFrameRef.current) {
          cancelAnimationFrame(playbackAnimationFrameRef.current);
        }
        playbackAnimationFrameRef.current =
          requestAnimationFrame(drawPlaybackFrame);
      })
      .catch(() => {
        setIsPlayingBack(false);
      });
  };

  const content = (
    <Overlay onClick={handleOverlayClick}>
      <Dialog onClick={(e) => e.stopPropagation()}>
        <CloseButton onClick={handleOverlayClick}>×</CloseButton>
        <Timer>{formatTime(seconds)}</Timer>
        <WaveformWrapper>
          <WaveformCanvas
            ref={waveformCanvasRef}
            width={480}
            height={160}
            onMouseDown={(e) => {
              if (!staticWaveformRef.current) return;
              const canvas = waveformCanvasRef.current;
              if (!canvas) return;
              const rect = canvas.getBoundingClientRect();
              const x = e.clientX - rect.left;
              const ratio = Math.min(1, Math.max(0, x / rect.width));
              const audio = ensurePlaybackAudio();
              if (!audio) return;
              const duration =
                playbackDurationRef.current || audio.duration || seconds || 1;
              isScrubbingRef.current = true;
              wasPlayingBeforeScrubRef.current = !audio.paused && !audio.ended;
              if (wasPlayingBeforeScrubRef.current) {
                audio.pause();
                setIsPlayingBack(false);
              }
              audio.currentTime = ratio * duration;
              drawPlaybackFrame();
            }}
            onMouseMove={(e) => {
              if (!isScrubbingRef.current) return;
              const canvas = waveformCanvasRef.current;
              if (!canvas) return;
              const rect = canvas.getBoundingClientRect();
              const x = e.clientX - rect.left;
              const ratio = Math.min(1, Math.max(0, x / rect.width));
              const audio = ensurePlaybackAudio();
              if (!audio) return;
              const duration =
                playbackDurationRef.current || audio.duration || seconds || 1;
              audio.currentTime = ratio * duration;
              drawPlaybackFrame();
            }}
            onMouseUp={() => {
              if (!isScrubbingRef.current) return;
              isScrubbingRef.current = false;
              const audio = playbackAudioRef.current;
              if (audio && wasPlayingBeforeScrubRef.current) {
                audio
                  .play()
                  .then(() => {
                    setIsPlayingBack(true);
                    if (playbackAnimationFrameRef.current) {
                      cancelAnimationFrame(playbackAnimationFrameRef.current);
                    }
                    playbackAnimationFrameRef.current =
                      requestAnimationFrame(drawPlaybackFrame);
                  })
                  .catch(() => {
                    setIsPlayingBack(false);
                  });
              } else {
                drawPlaybackFrame();
              }
              wasPlayingBeforeScrubRef.current = false;
            }}
            onMouseLeave={() => {
              if (!isScrubbingRef.current) return;
              isScrubbingRef.current = false;
              wasPlayingBeforeScrubRef.current = false;
              drawPlaybackFrame();
            }}
          />
        </WaveformWrapper>
        {hasRecording && (
          <PlaybackWrapper>
            <PlaybackButton
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                handleTogglePlayback();
              }}
            >
              {isPlayingBack ? (
                  <svg viewBox="0 0 24 24" style={{ width: '20px', height: '20px', fill:'#6777FF'  }}>
                  <rect x="6" y="4" width="4" height="16" fill='#6777FF' />
                  <rect x="14" y="4" width="4" height="16" fill='#6777FF' />
                </svg>
              ) : (
                <PlayIcon />
              )}
              <PlaybackLabel>{isPlayingBack ? "Pause" : "Play"}</PlaybackLabel>
            </PlaybackButton>
          </PlaybackWrapper>
        )}
        {!hasRecording ? (
          <>
            <RecordButtonWrapper>
              <RecordButton
                type="button"
                $recording={isRecording}
                onClick={(e) => {
                  e.stopPropagation();
                  startRecording();
                }}
              >
                <RecordButtonInner $recording={isRecording} />
              </RecordButton>
            </RecordButtonWrapper>
            <FooterText>1 min limit</FooterText>
          </>
        ) : (
          <ActionsRow>
            <SecondaryButton
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                if (playbackAnimationFrameRef.current) {
                  cancelAnimationFrame(playbackAnimationFrameRef.current);
                  playbackAnimationFrameRef.current = null;
                }
                if (playbackAudioRef.current) {
                  playbackAudioRef.current.pause();
                  playbackAudioRef.current = null;
                }
                if (playbackUrlRef.current) {
                  URL.revokeObjectURL(playbackUrlRef.current);
                  playbackUrlRef.current = null;
                }
                staticWaveformRef.current = null;
                playbackDurationRef.current = 0;
                setIsPlayingBack(false);
                setHasRecording(false);
                recordedBlobRef.current = null;
                setSeconds(0);
                waveformHistoryRef.current = null;
                const canvas = waveformCanvasRef.current;
                if (canvas) {
                  const ctx = canvas.getContext("2d");
                  if (ctx) {
                    const { width, height } = canvas;
                    ctx.clearRect(0, 0, width, height);
                    ctx.fillStyle = "#ffffff";
                    ctx.fillRect(0, 0, width, height);
                    ctx.strokeStyle = "#111827";
                    ctx.lineWidth = 2;
                    ctx.beginPath();
                    ctx.moveTo(0, height / 2);
                    ctx.lineTo(width, height / 2);
                    ctx.stroke();
                  }
                }
              }}
            >
              Start Over
            </SecondaryButton>
            <PrimaryGradientButton
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                if (!recordedBlobRef.current) return;
                const file = new File([recordedBlobRef.current], "recording.webm", {
                  type: "audio/webm",
                });
                onRecorded?.(file);
                onClose();
              }}
            >
              Save
            </PrimaryGradientButton>
          </ActionsRow>
        )}
      </Dialog>
    </Overlay>
  );

  if (typeof document === "undefined") {
    return content;
  }

  return createPortal(content, document.body);
};

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
`;

const Dialog = styled.div`
  width: 420px;
  max-width: 90vw;
  border-radius: 24px;
  background: #ffffff;
  padding: 32px 32px 24px 32px;
  box-shadow: 0 30px 80px rgba(15, 23, 42, 0.35);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 24px;
  position: relative;
`;

const CloseButton = styled.button`
  position: absolute;
  top: 12px;
  right: 12px;
  width: 28px;
  height: 28px;
  border-radius: 999px;
  border: none;
  background: #f3f4f6;
  color: #4b5563;
  font-size: 18px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Timer = styled.div`
  font-size: 16px;
  font-weight: 600;
  letter-spacing: 0.2em;
  color: #111827;
`;

const WaveformWrapper = styled.div`
  width: 100%;
  padding: 16px 0;
`;

const WaveformCanvas = styled.canvas`
  width: 100%;
  height: 160px;
  border-radius: 12px;
  background: #ffffff;
  border: 1px solid #e5e7eb;
`;

const PlaybackWrapper = styled.div`
  margin-top: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
`;

const PlaybackButton = styled.button`
  border-radius: 999px;
  border: 1px solid #e5e7eb;
  background: #ffffff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 10px 20px;
  gap: 8px;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.06);
`;

const PlaybackLabel = styled.div`
  font-size: 14px;
  font-weight: 500;
  color: #111827;
`;

const Divider = styled.div`
  width: 100%;
  height: 1px;
  background: #e5e7eb;
  margin-top: 8px;
`;

const RecordButtonWrapper = styled.div`
  margin-top: 12px;
`;

const RecordButton = styled.button<{ $recording?: boolean }>`
  width: 88px;
  height: 88px;
  border-radius: 999px;
  border: none;
  background: #f3f4f6;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 0 0 4px rgba(248, 113, 113, 0.16);
  transition: transform 0.1s ease, box-shadow 0.1s ease;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 0 0 6px rgba(248, 113, 113, 0.24);
  }
`;

const RecordButtonInner = styled.div<{ $recording?: boolean }>`
  width: ${p => (p.$recording ? "40px" : "72px")};
  height: ${p => (p.$recording ? "40px" : "72px")};
  border-radius: ${p => (p.$recording ? "10px" : "999px")};
  background: #f97316;
  transition: all 0.15s ease;
`;

const FooterText = styled.div`
  margin-top: 8px;
  font-size: 11px;
  color: #6b7280;
  text-align: center;
  text-transform: uppercase;
  letter-spacing: 0.08em;
`;

const ActionsRow = styled.div`
  width: 100%;
  display: flex;
  gap: 12px;
  margin-top: 4px;
`;

const SecondaryButton = styled.button`
  flex: 1;
  padding: 12px 0;
  border-radius: 999px;
  border: none;
  background: #f3f4f6;
  color: #111827;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
`;

const PrimaryGradientButton = styled.button`
  flex: 1;
  padding: 12px 0;
  border-radius: 999px;
  border: none;
  background: linear-gradient(-45deg, #6777FF 0%, #B7BFFF 100%);
  color: #ffffff;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
`;

export default RecordAudioModal;


